const nyDateFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
});

export const isValidPriceLevel = (value: unknown) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue > 0;
};

export const nyDateKey = (value?: string | number | Date | null) => {
    if (!value) return null;
    if (typeof value === "string") {
        const trimmed = value.trim();
        // Date-only ISO string (e.g. "2026-09-21") represents a specific session date.
        // Avoid parsing as UTC midnight which rolls back to previous day in New York timezone.
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            return trimmed;
        }
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return nyDateFormatter.format(date);
};

const nyTimeFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
});

const nySessionFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
});

export const isCurrentNySessionDate = (status?: any, fallbackDate?: string) => {
    const sourceDate = status?.levels_date || status?.timestamp || fallbackDate;
    const levelDate = nyDateKey(sourceDate);
    return !!levelDate && levelDate === nyDateKey(new Date());
};

export const isEarlyRegularSession = () => {
    const parts = nyTimeFormatter.formatToParts(new Date());
    const value = (type: string) => Number(parts.find((part) => part.type === type)?.value || 0);
    const minutes = value("hour") * 60 + value("minute");
    return minutes >= 9 * 60 + 30 && minutes < 10 * 60;
};

/**
 * Whether the regular US equity session is currently open. This is deliberately
 * a small client-side session check: live quote values still determine the
 * actual price state, while this flag tells the UI whether levels can change.
 */
export const isRegularMarketOpen = (date: Date = new Date()) => {
    const parts = nySessionFormatter.formatToParts(date);
    const value = (type: string) => parts.find((part) => part.type === type)?.value || "";
    const weekday = value("weekday");
    const minutes = Number(value("hour")) * 60 + Number(value("minute"));
    return weekday !== "Sat" && weekday !== "Sun" && minutes >= 9 * 60 + 30 && minutes < 16 * 60;
};

export interface AdaptiveLevelTone {
    label: string;
    bias: "long" | "short" | "neutral" | "forming";
    rowStyle: { backgroundColor: string; boxShadow?: string };
    priceClass: string;
    lowerClass: string;
    upperClass: string;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const getAdaptiveLevelTone = ({
    price,
    lowerLevel,
    upperLevel,
    levelsReady,
    earlySession = false,
    marketOpen = true,
}: {
    price: number;
    lowerLevel: number;
    upperLevel: number;
    levelsReady: boolean;
    earlySession?: boolean;
    marketOpen?: boolean;
}): AdaptiveLevelTone => {
    if (!marketOpen) {
        const hasFixedLevels = levelsReady && Number.isFinite(lowerLevel) && Number.isFinite(upperLevel) && upperLevel > lowerLevel;
        return {
            label: hasFixedLevels ? "Market closed · levels fixed" : "Market closed · awaiting session",
            bias: hasFixedLevels ? "neutral" : "forming",
            rowStyle: {
                backgroundColor: hasFixedLevels ? "rgba(148, 163, 184, 0.035)" : "rgba(245, 158, 11, 0.035)",
            },
            priceClass: hasFixedLevels ? "text-white/70" : "text-amber-100/60",
            lowerClass: "text-rose-300/50",
            upperClass: "text-emerald-300/50",
        };
    }

    const neutral: AdaptiveLevelTone = {
        label: levelsReady ? "Inside levels" : "Levels forming",
        bias: levelsReady ? "neutral" : "forming",
        rowStyle: {
            backgroundColor: levelsReady ? "rgba(148, 163, 184, 0.025)" : "rgba(245, 158, 11, 0.045)",
        },
        priceClass: levelsReady ? "text-white/85" : "text-amber-100/70",
        lowerClass: "text-rose-300/65",
        upperClass: "text-emerald-300/65",
    };

    if (!levelsReady || !Number.isFinite(price) || !Number.isFinite(lowerLevel) || !Number.isFinite(upperLevel) || upperLevel <= lowerLevel) {
        return neutral;
    }

    const span = Math.max(upperLevel - lowerLevel, Math.abs(upperLevel) * 0.001, 0.01);
    const position = (price - lowerLevel) / span;
    const muted = earlySession ? 0.62 : 1;
    const edgeAlpha = (value: number) => clamp(value * muted, 0.035, earlySession ? 0.18 : 0.3);

    if (position >= 1) {
        const alpha = edgeAlpha(0.12 + (position - 1) * 0.16);
        return {
            label: "Above long level",
            bias: "long",
            rowStyle: {
                backgroundColor: `rgba(16, 185, 129, ${alpha})`,
                boxShadow: `inset 3px 0 rgba(52, 211, 153, ${clamp(alpha + 0.14, 0.2, 0.48)})`,
            },
            priceClass: "text-emerald-200",
            lowerClass: "text-rose-300/55",
            upperClass: "text-emerald-300 font-bold",
        };
    }

    if (position <= 0) {
        const alpha = edgeAlpha(0.12 + Math.abs(position) * 0.16);
        return {
            label: "Below short level",
            bias: "short",
            rowStyle: {
                backgroundColor: `rgba(244, 63, 94, ${alpha})`,
                boxShadow: `inset 3px 0 rgba(251, 113, 133, ${clamp(alpha + 0.14, 0.2, 0.48)})`,
            },
            priceClass: "text-rose-200",
            lowerClass: "text-rose-300 font-bold",
            upperClass: "text-emerald-300/55",
        };
    }

    if (position >= 0.72) {
        const alpha = edgeAlpha(0.04 + ((position - 0.72) / 0.28) * 0.07);
        return {
            label: "Near long level",
            bias: "long",
            rowStyle: { backgroundColor: `rgba(16, 185, 129, ${alpha})` },
            priceClass: "text-emerald-200/90",
            lowerClass: "text-rose-300/55",
            upperClass: "text-emerald-300 font-semibold",
        };
    }

    if (position <= 0.28) {
        const alpha = edgeAlpha(0.04 + ((0.28 - position) / 0.28) * 0.07);
        return {
            label: "Near short level",
            bias: "short",
            rowStyle: { backgroundColor: `rgba(244, 63, 94, ${alpha})` },
            priceClass: "text-rose-200/90",
            lowerClass: "text-rose-300 font-semibold",
            upperClass: "text-emerald-300/55",
        };
    }

    return neutral;
};

export const hasFreshLevels = (status?: any, fallbackDate?: string) => {
    const sourceDate = status?.levels_date || status?.timestamp || fallbackDate;
    const levelDate = nyDateKey(sourceDate);
    return !!levelDate;
};
